/* Header interactivity: desktop mega menu (<header-mega-nav>) and the mobile
   drill-down menu drawer (<header-nav-drawer>). Motion goes through Theme.motion
   (assets/animations.js) and is skipped entirely under prefers-reduced-motion. */
(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Theme.motion keeps the last keyframe applied (fill: both); cancel once finished so
  // the element's own CSS state takes over again.
  const animate = (element, keyframes, options = {}) => {
    if (!element || reducedMotion() || !window.Theme || !window.Theme.motion) return Promise.resolve();
    return window.Theme.motion
      .animate(element, keyframes, options)
      .finished.catch(() => {})
      .then(() => element.getAnimations().forEach((animation) => animation.cancel()));
  };

  class HeaderMegaNav extends HTMLElement {
    constructor() {
      super();
      this.openItem = null;
      this.openTimer = null;
      this.closeTimer = null;
      this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
    }

    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-mega-item]'));
      this.header = this.closest('.header-wrapper');
      this.cleanups = [];

      this.items.forEach((item) => {
        const toggle = item.querySelector('[data-mega-toggle]');
        const link = item.querySelector('[data-mega-link]');

        const onEnter = () => {
          if (!this.hoverQuery.matches) return;
          clearTimeout(this.closeTimer);
          clearTimeout(this.openTimer);
          const delay = this.openItem ? 0 : 80;
          this.openTimer = setTimeout(() => this.open(item), delay);
        };
        const onLeave = () => {
          if (!this.hoverQuery.matches) return;
          clearTimeout(this.openTimer);
          clearTimeout(this.closeTimer);
          this.closeTimer = setTimeout(() => {
            if (this.openItem === item) this.close();
          }, 160);
        };
        const onToggle = (event) => {
          event.preventDefault();
          if (this.openItem === item) {
            this.close();
          } else {
            this.open(item, { focusFirst: event.detail === 0 });
          }
        };
        // Touch / coarse pointers: first tap on a link with a panel opens it, second tap navigates.
        const onLinkClick = (event) => {
          if (this.hoverQuery.matches || this.openItem === item) return;
          event.preventDefault();
          this.open(item);
        };

        item.addEventListener('mouseenter', onEnter);
        item.addEventListener('mouseleave', onLeave);
        if (toggle) toggle.addEventListener('click', onToggle);
        if (link) link.addEventListener('click', onLinkClick);

        this.cleanups.push(() => {
          item.removeEventListener('mouseenter', onEnter);
          item.removeEventListener('mouseleave', onLeave);
          if (toggle) toggle.removeEventListener('click', onToggle);
          if (link) link.removeEventListener('click', onLinkClick);
        });
      });

      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusout', this.onFocusOut);

      // Warm the (lazy) promo images on first intent so a panel never opens onto blank tiles.
      this.warmImages = () => {
        this.querySelectorAll('[data-mega-panel] img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      };
      this.addEventListener('pointerenter', this.warmImages, { once: true });
      this.addEventListener('focusin', this.warmImages, { once: true });
    }

    disconnectedCallback() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      (this.cleanups || []).forEach((cleanup) => cleanup());
      this.cleanups = [];
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('focusout', this.onFocusOut);
      if (this.warmImages) {
        this.removeEventListener('pointerenter', this.warmImages);
        this.removeEventListener('focusin', this.warmImages);
      }
      document.removeEventListener('click', this.onDocumentClick);
      this.openItem = null;
    }

    open(item, { focusFirst = false } = {}) {
      if (this.openItem === item) return;
      const previous = this.openItem;
      if (previous) this.hideItem(previous, false);

      const panel = item.querySelector('[data-mega-panel]');
      const toggle = item.querySelector('[data-mega-toggle]');
      if (!panel) return;

      this.openItem = item;
      item.classList.add('is-open');
      panel.hidden = false;
      this.setBridge(item);
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.onDocumentClick);

      if (!previous) {
        animate(panel, { opacity: [0, 1], transform: ['translateY(-6px)', 'translateY(0)'] }, { duration: 0.22, easing: 'ease-out' });
      }

      if (focusFirst) {
        const first = panel.querySelector('a[href]');
        if (first) first.focus();
      }
    }

    setBridge(item) {
      const link = item.querySelector('[data-mega-link]');
      if (!this.header || !link) return;
      const gap = this.header.getBoundingClientRect().bottom - link.getBoundingClientRect().bottom;
      item.style.setProperty('--mega-bridge', `${Math.max(0, Math.ceil(gap))}px`);
    }

    hideItem(item, animated = true) {
      const panel = item.querySelector('[data-mega-panel]');
      const toggle = item.querySelector('[data-mega-toggle]');
      item.classList.remove('is-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (!panel) return;
      if (!animated || reducedMotion()) {
        panel.hidden = true;
        return;
      }
      animate(panel, { opacity: [1, 0] }, { duration: 0.15, easing: 'ease-in' }).then(() => {
        if (this.openItem !== item) panel.hidden = true;
      });
    }

    close({ returnFocus = false } = {}) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      const item = this.openItem;
      if (!item) return;
      this.openItem = null;
      document.removeEventListener('click', this.onDocumentClick);
      this.hideItem(item);
      if (returnFocus) {
        const target = item.querySelector('[data-mega-toggle]') || item.querySelector('[data-mega-link]');
        if (target) target.focus();
      }
    }

    onDocumentClick(event) {
      if (!this.openItem) return;
      if (this.openItem.contains(event.target)) return;
      this.close();
    }

    onKeydown(event) {
      if (event.key !== 'Escape' || !this.openItem) return;
      event.preventDefault();
      this.close({ returnFocus: true });
    }

    onFocusOut(event) {
      if (!this.openItem) return;
      const next = event.relatedTarget;
      if (next && this.openItem.contains(next)) return;
      // Pointer users keep the panel while hovering; keyboard focus leaving the item closes it.
      if (!next && this.openItem.matches(':hover')) return;
      this.close();
    }
  }

  class HeaderNavDrawer extends HTMLElement {
    constructor() {
      super();
      this.onKeydown = this.onKeydown.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onToggleClick = this.onToggleClick.bind(this);
      this.onDrawerClick = this.onDrawerClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
    }

    onDocumentClick(event) {
      if (this.isOpen && !this.contains(event.target)) this.close();
    }

    connectedCallback() {
      this.toggle = this.querySelector('[data-drawer-toggle]');
      this.drawer = this.querySelector('[data-drawer]');
      this.root = this.querySelector('[data-drawer-view="root"]');
      this.sectionHeader = this.closest('.section-header');
      this.headerWrapper = this.closest('.header-wrapper');
      this.labelOpen = this.querySelector('[data-label-open]');
      this.labelClose = this.querySelector('[data-label-close]');
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      this.isMegaDesktop = !!this.closest('.header-bar--menu-mega');
      this.activeView = null;
      this.activeTrigger = null;

      if (this.toggle) this.toggle.addEventListener('click', this.onToggleClick);
      if (this.drawer) this.drawer.addEventListener('click', this.onDrawerClick);
      this.desktopQuery.addEventListener('change', this.onResize);
    }

    disconnectedCallback() {
      if (this.toggle) this.toggle.removeEventListener('click', this.onToggleClick);
      if (this.drawer) this.drawer.removeEventListener('click', this.onDrawerClick);
      this.desktopQuery.removeEventListener('change', this.onResize);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      if (this.isOpen) this.unlockPage();
    }

    get isOpen() {
      return this.toggle && this.toggle.getAttribute('aria-expanded') === 'true';
    }

    onToggleClick(event) {
      event.preventDefault();
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    onResize() {
      if (this.isOpen && this.desktopQuery.matches && this.isMegaDesktop) this.close({ returnFocus: false });
    }

    lockPage() {
      document.body.classList.add('overflow-hidden');
      if (this.sectionHeader) this.sectionHeader.classList.add('menu-open');
    }

    unlockPage() {
      document.body.classList.remove('overflow-hidden');
      if (this.sectionHeader) this.sectionHeader.classList.remove('menu-open');
    }

    updateTop() {
      const anchor = this.headerWrapper || this;
      const bottom = Math.max(0, Math.round(anchor.getBoundingClientRect().bottom));
      this.drawer.style.setProperty('--nav-drawer-top', `${bottom}px`);
    }

    open() {
      if (!this.drawer || this.isOpen) return;
      this.updateTop();
      this.showView(this.root, false);
      this.drawer.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
      this.drawer.hidden = false;
      this.drawer.scrollTop = 0;
      this.toggle.setAttribute('aria-expanded', 'true');
      if (this.labelOpen) this.labelOpen.hidden = true;
      if (this.labelClose) this.labelClose.hidden = false;
      this.lockPage();
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);

      animate(this.drawer, { transform: ['translateX(-100%)', 'translateX(0)'] }, { duration: 0.35, easing: [0.39, 0.575, 0.565, 1] });

      if (typeof window.trapFocus === 'function') {
        // Trap inside the whole component so the header's close (X) toggle stays reachable.
        window.trapFocus(this, this.drawer.querySelector('.nav-drawer__row') || this.toggle);
      }
    }

    close({ returnFocus = true } = {}) {
      if (!this.drawer || !this.isOpen) return;
      this.toggle.setAttribute('aria-expanded', 'false');
      if (this.labelOpen) this.labelOpen.hidden = false;
      if (this.labelClose) this.labelClose.hidden = true;
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(returnFocus ? this.toggle : null);
      else if (returnFocus) this.toggle.focus();

      const finish = () => {
        if (this.isOpen) return;
        this.drawer.hidden = true;
        this.showView(this.root, false);
        this.unlockPage();
      };
      if (reducedMotion()) {
        finish();
      } else {
        animate(this.drawer, { transform: ['translateX(0)', 'translateX(-100%)'] }, { duration: 0.3, easing: 'ease-in' }).then(finish);
      }
    }

    showView(view, animated = true, direction = 1) {
      if (!view) return;
      const views = this.querySelectorAll('[data-drawer-view]');
      views.forEach((candidate) => {
        candidate.hidden = candidate !== view;
      });
      this.querySelectorAll('[data-drawer-open-view]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', trigger.dataset.drawerOpenView === view.id ? 'true' : 'false');
      });
      this.activeView = view === this.root ? null : view;
      if (animated) {
        animate(view, { opacity: [0, 1], transform: [`translateX(${direction * 24}px)`, 'translateX(0)'] }, { duration: 0.25, easing: 'ease-out' });
      }
    }

    refreshTrap(focusTarget) {
      if (typeof window.trapFocus === 'function') window.trapFocus(this, focusTarget);
      else if (focusTarget) focusTarget.focus();
    }

    onDrawerClick(event) {
      const openTrigger = event.target.closest('[data-drawer-open-view]');
      if (openTrigger && this.contains(openTrigger)) {
        event.preventDefault();
        const view = this.querySelector(`#${CSS.escape(openTrigger.dataset.drawerOpenView)}`);
        if (!view) return;
        this.activeTrigger = openTrigger;
        this.showView(view, true, 1);
        this.drawer.scrollTop = 0;
        this.refreshTrap(view.querySelector('[data-drawer-back]'));
        return;
      }

      const back = event.target.closest('[data-drawer-back]');
      if (back && this.contains(back)) {
        event.preventDefault();
        this.goBack();
        return;
      }

      const summary = event.target.closest('.nav-drawer__accordion-summary');
      if (summary && this.contains(summary)) {
        event.preventDefault();
        this.toggleAccordion(summary.parentElement);
      }
    }

    goBack() {
      const trigger = this.activeTrigger;
      this.showView(this.root, true, -1);
      this.refreshTrap(trigger || this.root.querySelector('.nav-drawer__row'));
      this.activeTrigger = null;
    }

    toggleAccordion(details) {
      const content = details.querySelector('.nav-drawer__accordion-content');
      if (!content || details.dataset.animating === 'true') return;

      if (!details.open) {
        details.open = true;
        if (reducedMotion()) return;
        const height = content.scrollHeight;
        details.dataset.animating = 'true';
        animate(content, { height: ['0px', `${height}px`], opacity: [0, 1] }, { duration: 0.3, easing: 'ease-out' }).then(() => {
          delete details.dataset.animating;
        });
      } else {
        if (reducedMotion()) {
          details.open = false;
          return;
        }
        const height = content.scrollHeight;
        details.dataset.animating = 'true';
        animate(content, { height: [`${height}px`, '0px'], opacity: [1, 0] }, { duration: 0.25, easing: 'ease-in' }).then(() => {
          details.open = false;
          delete details.dataset.animating;
        });
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      if (this.activeView) {
        this.goBack();
      } else {
        this.close();
      }
    }
  }

  if (!customElements.get('header-mega-nav')) customElements.define('header-mega-nav', HeaderMegaNav);
  if (!customElements.get('header-nav-drawer')) customElements.define('header-nav-drawer', HeaderNavDrawer);
})();
